import { html, LitElement } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import lottie from 'lottie-web';

import { buildURL } from 'Utils/api-fetch-request';

import Styles from './vm-chatbot-skeleton.scss';

type VmChatbotSkeletonType = 'inline' | 'modal';

const _MESSAGES: Record<VmChatbotSkeletonType, string[]> = {
    inline: ['Summarizing...'],
    modal: ['Understanding Vumedi videos...', 'Searching most recent updates...', 'Finding relevant videos...'],
};

const _SKELETON_LINE_COUNT = 12;
const _TEASER_INTERVAL = 3000;

@customElement('vm-chatbot-skeleton')
export class VmChatbotSkeleton extends LitElement {
    @state()
    private _activeTeaserText?: string;

    @property({ type: Boolean, attribute: 'data-hidden' })
    dataHidden = false;

    @property({ type: String, attribute: 'data-type' })
    dataType: VmChatbotSkeletonType = 'inline';

    static override styles = Styles;

    private _activeTeaserInterval: ReturnType<typeof setInterval> | null = null;

    override firstUpdated() {
        this.loadAnimation();
        this.setTeaserText();
    }

    getMessages() {
        // The attribute isn't validated at runtime, so an unknown value falls back to inline
        return _MESSAGES[this.dataType] ?? _MESSAGES.inline;
    }

    loadAnimation() {
        const animationPath = document.querySelector<HTMLMetaElement>('meta[name="lottie-ai-answer"]')?.content;
        const container = this.renderRoot.querySelector<HTMLElement>('.teaser-message-avatar');

        if (!animationPath || !container) {
            return;
        }

        const animation = lottie.loadAnimation({
            name: `answer-${this.dataType}`,
            container,
            renderer: 'svg',
            loop: true,
            autoplay: true,
            path: buildURL(animationPath, { name: `vm-chatbot-skeleton-${this.dataType}` }),
        });

        animation.addEventListener('DOMLoaded', () => {
            animation.setSpeed(0.66);
            animation.playSegments([45, 164], true);
        });
    }

    setTeaserText() {
        if (this._activeTeaserInterval) {
            clearInterval(this._activeTeaserInterval);
            this._activeTeaserInterval = null;
        }

        const messages = this.getMessages();

        if (messages.length === 1) {
            return;
        }

        let activeTeaserIndex = 0;

        this._activeTeaserInterval = setInterval(() => {
            activeTeaserIndex = ++activeTeaserIndex % messages.length;
            this._activeTeaserText = messages[activeTeaserIndex];
        }, _TEASER_INTERVAL);
    }

    renderSkeletonLines() {
        const lines = [];

        for (let i = 0; i < _SKELETON_LINE_COUNT; i++) {
            lines.push(html`<div class="line"></div>`);
        }

        return lines;
    }

    override render() {
        const teaserText = this._activeTeaserText ?? this.getMessages()[0];

        return html`
            <div class="teaser-message-wrap">
                <div class="teaser-message-avatar"></div>
                <div class="teaser-message-text">${teaserText}</div>
            </div>
            <div class="lines-wrap">${this.renderSkeletonLines()}</div>
        `;
    }
}
